import React from 'react';
import { Link } from 'react-router-dom';
import { Rocket, KeyRound, Fingerprint, FileCode2, PenLine, Send, Inbox, ListTree, Tags } from 'lucide-react';
import {
  XbPage, WithScheme, SchemeHeading, ProvisionalNotice, Card, C, CodeBlock, Table,
} from './common';
import { endpointsFor, label, schemeBase } from '../../utils/crossBorder';

const HOST = 'https://<your-sandbox-host>';

/**
 * How a participant connects to the chosen scheme: credentials, its identity
 * in the scheme, the message versions, signing, sending and answers, being
 * paid, every endpoint and header. Written from the profile, so it reads right
 * for a Sandbox-format scheme and for a Montran-built one such as PAPSS.
 */
export default function XbGettingStarted() {
  return (
    <XbPage icon={Rocket} title="Getting started" wide
      intro="Everything your system needs to reach the scheme, in the order you need it.">
      <WithScheme>{(scheme) => <Guide scheme={scheme} />}</WithScheme>
    </XbPage>
  );
}

function Step({ n, icon: Icon, title, children }) {
  return (
    <section className="bg-white border border-gray-200 rounded-xl p-5 flex gap-4">
      <span className="w-8 h-8 shrink-0 rounded-full bg-primary-50 text-primary-700 font-semibold flex items-center justify-center">{n}</span>
      <div className="min-w-0 flex-1 space-y-2">
        <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary-600" aria-hidden="true" /> {title}</h2>
        <div className="text-sm text-gray-700 space-y-2">{children}</div>
      </div>
    </section>
  );
}

function Guide({ scheme }) {
  const s = scheme.profile;
  const polls = s.offers?.polls;
  const montran = s.wire?.envelope === 'MONTRAN_MESSAGE';
  const base = schemeBase(s.id);
  const v = s.messages || {};

  const sendExample = polls
    ? `# 1. Sign the payment as your institution (or sign it with your own key)
curl -X POST "${HOST}${base}/papss/sign" \\
  -H "X-API-Key: <your-api-key>" -H "Content-Type: application/xml" \\
  --data-binary @pacs008.xml > signed.xml

# 2. Send it: the answer is a receipt
curl -i -X POST "${HOST}${base}/papss/Message" \\
  -H "X-API-Key: <your-api-key>" -H "Content-Type: application/xml" \\
  --data-binary @signed.xml
# → 200, X-PAPSSRTP-ReqSts: ACTC, X-PAPSSRTP-MessageSeq: 41

# 3. Poll for the status, then confirm it
curl -i "${HOST}${base}/papss/Message" -H "X-API-Key: <your-api-key>"
curl -X POST "${HOST}${base}/papss/MessageAck" \\
  -H "X-API-Key: <your-api-key>" -H "X-PAPSSRTP-MessageSeq: 41"
# → Stored`
    : `# Sign the payment as your institution (or sign it with your own key)
curl -X POST "${HOST}/api/signWithoutSaving" \\
  -H "X-API-Key: <your-api-key>" -H "Content-Type: application/xml" \\
  --data-binary @pacs008.xml > signed.xml

# Send it: the answer is the scheme's signed pacs.002
curl -i -X POST "${HOST}${base}/clearing/credit" \\
  -H "X-API-Key: <your-api-key>" -H "Content-Type: application/xml" \\
  --data-binary @signed.xml`;

  const envelope = `<hdr:Message xmlns:hdr="urn:montran:message.01">
  <hdr:AppHdr xmlns="urn:iso:std:iso:20022:tech:xsd:head.001.001.01">
    <Fr><FIId><FinInstnId><ClrSysMmbId>
      <ClrSysId><Prtry>${s.wire?.clearingSystem || ''}</Prtry></ClrSysId><MmbId>${scheme.memberId || ''}</MmbId>
    </ClrSysMmbId></FinInstnId></FIId></Fr>
    <To> … <MmbId>${s.wire?.hubMemberId || ''}</MmbId> … </To>
    <BizMsgIdr>your message id</BizMsgIdr>
    <MsgDefIdr>${v['pacs.008'] || ''}</MsgDefIdr>${s.wire?.bizSvc ? `\n    <BizSvc>${s.wire.bizSvc}</BizSvc>` : ''}
    <CreDt>2026-10-25T08:00:00Z</CreDt>
    <Sgntr><ds:Signature> … one reference, URI="" … </ds:Signature></Sgntr>
  </hdr:AppHdr>
  <hdr:FIToFICstmrCdtTrf xmlns="urn:iso:std:iso:20022:tech:xsd:${v['pacs.008'] || ''}">
    <GrpHdr> … </GrpHdr>
    <CdtTrfTxInf> … </CdtTrfTxInf>
  </hdr:FIToFICstmrCdtTrf>
</hdr:Message>`;

  const requestHeaders = [
    ['X-API-Key', 'every request', 'Your tenant API key. The portal uses your signed-in session instead.'],
    ['X-Cert-Scenario', 'a payment', <>Forces the scheme's answer for a test: <C>REJECT</C> or <C>REJECT:AM04</C>. Excluded from certification.</>],
    ...(s.offers?.quotes ? [['X-Cert-Scenario', 'a quote', <><C>FX_DECLINE</C> or <C>FX_EXPIRE:10</C>, to see a provider decline or a quote run out.</>]] : []),
    ...(s.settlement?.windows?.length ? [['X-Cert-Clock', 'a payment', 'The time the switch should take as now, to test cut-offs and value dates.']] : []),
  ];
  const responseHeaders = [
    ['X-Scheme-Profile', 'The profile and version that answered, e.g. ' + s.ref],
    ['X-Profile-Findings', 'Rules that warned or informed without refusing'],
    ['X-Profile-Refusal', 'Which kind of check refused the payment, and why'],
    ...(s.offers?.quotes ? [['X-FX-Quote', 'The quote the payment used, or released']] : []),
    ...(s.offers?.liquidity ? [['X-Liquidity', 'What the payment did to your position']] : []),
    ...(s.settlement?.windows?.length ? [['X-Settlement', 'When and on what value date the payment settles']] : []),
    ...(s.screening ? [['X-Screening', 'A potential or confirmed watch-list match, and when a hold is decided']] : []),
    ...(polls ? [
      ['X-PAPSSRTP-ReqSts', 'ACTC when the hub took the message in; RJCT with the hub\'s refusal in the body'],
      ['X-PAPSSRTP-MessageSeq', 'The inbox sequence number: of the answer to come (on a submission), or of the message polled'],
      ['X-PAPSSRTP-MessageType', 'What the polled message is: pacs.008, pacs.002…'],
      ['X-PAPSSRTP-RemainingOutputs', 'How many more messages wait'],
      ['X-PAPSSRTP-PossibleDuplicate', 'true when the message was handed out before and not confirmed'],
    ] : []),
  ];

  return (
    <>
      <SchemeHeading summary={s} />
      <ProvisionalNotice summary={s} />

      <Step n={1} icon={KeyRound} title="Your credentials">
        <p>The same as for ISO 20022: your tenant API key on every request, and the signing certificate you
          registered under <Link to="/iso20022/certificates" className="underline">ISO 20022 → Signing
          Certificates</Link>. The scheme verifies your messages against it.</p>
      </Step>

      <Step n={2} icon={Fingerprint} title="Who you are in the scheme">
        <p>You send from {scheme.homeCountry}, in {scheme.homeCurrency}
          {scheme.memberId ? <>, as member <C>{scheme.memberId}</C></> : null}.
          {montran && <> Every message you send is addressed from <C>{scheme.memberId}</C> to the scheme,
            {' '}<C>{s.wire.hubMemberId}</C>; one from another member id is refused with <C>RC01</C>.</>}
        </p>
        <p>Your base path is <C>{base}</C>. The domestic switch's endpoints do not take cross-border payments.</p>
      </Step>

      <Step n={3} icon={FileCode2} title="Messages in the scheme's versions">
        <p>The scheme takes {Object.entries(v).map(([f, ver], i) => <span key={f}>{i ? ', ' : ''}<C>{ver}</C></span>)}.
          A message in another version is refused before anything else is checked.</p>
        <p>Its <Link to="/xb/profile" className="underline">profile</Link> lists the {s.rules} rules each payment must
          meet; <Link to="/xb/samples" className="underline">Message Samples</Link> has messages that meet them.</p>
      </Step>

      <Step n={4} icon={PenLine} title="Signing">
        <p>{label('signature', s.wire?.signature)}.</p>
        {montran ? (
          <>
            <p>The message travels in the scheme's own envelope: the header and the message body side by side
              in <C>Message</C>, with no <C>{'<Document>'}</C>. The signature sits in <C>AppHdr/Sgntr</C> and covers the
              whole message with one reference (<C>URI=""</C>, enveloped-signature then C14N 1.1); the key is named
              by its subject and issuer serial. <C>POST {base}/papss/sign</C> builds the envelope from your
              {' '}<C>{'<Document>'}</C> and signs it with your key in the Sandbox.</p>
            <CodeBlock>{envelope}</CodeBlock>
          </>
        ) : (
          <p>As on the domestic switch: see the <Link to="/iso20022/signing-guide" className="underline">ISO 20022
            Signing Guide</Link>. <C>POST /api/signWithoutSaving</C> signs a message with your key in the Sandbox.</p>
        )}
      </Step>

      <Step n={5} icon={Send} title="Sending, and the answer">
        {polls ? (
          <p>Everything goes to <C>POST {base}/papss/Message</C>. The hub answers at once with a receipt
            (<C>X-PAPSSRTP-ReqSts: ACTC</C> and the sequence number of the answer to come); the status itself,
            a pacs.002 for the group from <C>{s.wire.hubMemberId}</C>, waits in your inbox. Poll
            {' '}<C>GET {base}/papss/Message</C> at least every {s.wire.maxPollSeconds || 5} s and confirm each
            message with <C>MessageAck</C>; one not confirmed comes back marked as a possible duplicate.</p>
        ) : (
          <p>Send to <C>POST {base}/clearing/credit</C>. The answer is the scheme's signed pacs.002, in the
            response{s.offers?.holds ? '; a payment held for screening is answered PDNG, and its final status follows on your payment-status callback' : ''}.
            Ask after a payment with <C>POST {base}/clearing/status-request</C>.</p>
        )}
        <CodeBlock>{sendExample}</CodeBlock>
      </Step>

      <Step n={6} icon={Inbox} title="Being paid">
        <p>{polls
          ? <>A payment to you waits in your inbox. Answer it with <C>{s.replies.incomingReply}</C> or <C>RJCT</C> and a
            {' '}reason, {label('level', s.replies.level)}, within {s.replies.responseSeconds} s; the scheme then confirms
            {' '}<C>{s.replies.confirmation || 'ACSC'}</C>. No answer in time: the scheme answers <C>TM01</C>, and a late
            {' '}answer is refused with <C>MS03</C>.</>
          : <>A payment to you is delivered to your incoming-credit callback. Answer it with <C>{s.replies.incomingReply}</C> or
            {' '}<C>RJCT</C> and a reason, {label('level', s.replies.level)}, within {s.replies.responseSeconds} s.</>}
          {' '}<Link to="/xb/incoming" className="underline">Incoming Payments</Link> sends you one for each scenario
          and says whether you answered as the scheme expects.</p>
      </Step>

      <Card title="Endpoints" icon={ListTree}>
        <Table head={['', 'Path', 'What it does']} mono={[0, 1]}
          rows={endpointsFor(s).map((e) => [e.method, e.path, e.what])} />
      </Card>

      <Card title="Headers" icon={Tags}>
        <p className="text-sm font-medium text-gray-800 mb-2">You send</p>
        <Table head={['Header', 'On', 'What it does']} mono={[0]} rows={requestHeaders} />
        <p className="text-sm font-medium text-gray-800 mt-5 mb-2">The scheme answers with</p>
        <Table head={['Header', 'What it says']} mono={[0]} rows={responseHeaders} />
      </Card>
    </>
  );
}
